<template>
  <div class="map-viewer-area">
    <!-- Container do Mapa sem bordas ou cards -->
    <div class="map-container">

      <!-- Loading Overlay -->
      <div v-if="loading" class="map-loading-overlay">
        <q-spinner-dots size="40px" color="primary" />
        <div class="text-subtitle2 q-mt-md">
          {{ this.$gettext('LOADING_MAP') }}
        </div>
      </div>
    <MapCompleto />

    <!-- Renderização dinâmica dos Postos Avançados -->
    <div
      v-for="posto in postosAvancadosVisiveis"
      :key="posto.id"
      class="pin-wrapper"
      :style="calcularPosicao(posto)"
    >
      <PinPostoAvancado
        class="map-container-pin"
        :status="getStatusPin(posto.criticality)"
        :numero="posto.numero"
        @click="onPostoClick(posto)"
      />
      <!-- Tooltip -->
      <div class="pin-tooltip" :class="[`tooltip-${posto.criticality}`, { 'tooltip-bottom': isTopPin(posto) }]">
        <div class="tooltip-title">{{ formatarNomePosto(posto) }}</div>
        <div v-if="posto.criticality === 'red'" class="tooltip-status critico">
          Crítico &#x1F4A3;
        </div>
        <div v-else-if="posto.criticality === 'yellow'" class="tooltip-status atencao">
          Atenção &#x1F4A1;
        </div>
      </div>
    </div>

    <!-- Render the modal -->
    <ModalDetalhesPostoAvancado
      v-model="modalPostoAvancado.visible"
      :data="modalPostoAvancado.data"
      @close="closePostoAvancadoModal"
    />
  </div>
</div>
</template>

<script>
import { Component, Vue, Prop } from 'vue-property-decorator'
import IndustrialMapContainer from './IndustrialMapContainer.vue'
import MapCompleto from './MapCompleto.vue'
import PinPostoAvancado from './icons/PinPostoAvancado.vue'
import ModalDetalhesPostoAvancado from 'src/components/industrialMap/modals/ModalDetalhesPostoAvancado.vue'

@Component({
  name: 'MapViewerArea',
  components: {
    IndustrialMapContainer,
    MapCompleto,
    PinPostoAvancado,
    ModalDetalhesPostoAvancado
  }
})
class MapViewerArea extends Vue {
  @Prop({ type: Array, default: () => [] })
  postos

  @Prop({ type: Array, default: () => [] })
  clientes

  @Prop({ type: Object, default: null })
  clienteSelecionado

  @Prop({ type: Object, default: () => ({}) })
  filtrosAtivos

  @Prop({ type: Boolean, default: false })
  loading

  modalPostoAvancado = {
    visible: false,
    data: null
  }

  // Posições em porcentagem relativa ao viewBox do SVG (1514 x 921)
  posicoesFixas = {
    1: { top: 50.8, left: 47.6 }, // Toucador
    2: { top: 62.8, left: 77 }, // Planta piloto
    3: { top: 62.4, left: 21.5 }, // CPD
    4: { top: 16.4, left: 47 }, // Agulhas
    5: { top: 16.4, left: 35.6 }, // Cat Gut
    6: { top: 78.46, left: 49.6 }, // QLD
    7: { top: 38.4, left: 77 }, // DPA (areaCode: 16)
    8: { top: 68.4, left: 39.3 }, // Higiene Oral
    9: { top: 74.8, left: 77.4 }, // INNOVATIVE MEDICINE
    10: { top: 49, left: 70 }, // Band-Aid
    11: { top: 52.64, left: 70.91 }, // Novo Posto
    12: { top: 58, left: 34.6 }, // Sanpro
    13: { top: 24, left: 63 }, // Restaurante
    14: { top: 50.4, left: 54.6 } // Kenvue OTC
  }

  get postosAvancadosVisiveis () {
    // Use postos from props instead of local postosAvancados
    const postosData = this.postos.length > 0 ? this.postos : []

    return postosData.filter(posto => {
      const clienteId = typeof this.clienteSelecionado === 'object' && this.clienteSelecionado !== null
        ? this.clienteSelecionado.id
        : this.clienteSelecionado

      if (clienteId) {
        if (posto.clientId !== clienteId) {
          return false
        }
      }

      // Use numero property that was extracted from name during loading
      const numeroPostoKey = posto.numero

      const posicaoFixa = this.posicoesFixas[numeroPostoKey]

      if (posicaoFixa && posicaoFixa.top !== 0 && posicaoFixa.left !== 0) {
        return true
      }

      if (!posto.coordinates) {
        return false
      }
      if (posto.hasInvalidCoordinates) {
        return false
      }
      if (posto.coordinates.latitude === null || posto.coordinates.longitude === null) {
        return false
      }
      return true
    })
  }

  getStatusPin (criticality) {
    const statusMap = {
      red: 'critico',
      yellow: 'atencao',
      green: 'normal'
    }
    return statusMap[criticality] || 'normal'
  }

  calcularPosicao (posto) {
    // Use numero property that was extracted from name during loading
    const numeroPostoKey = posto.numero

    const posicaoFixa = this.posicoesFixas[numeroPostoKey]

    if (!posicaoFixa || (posicaoFixa.top === 0 && posicaoFixa.left === 0)) {
      console.warn(`PA ${numeroPostoKey} (${posto.name}) não tem posição definida`)
      return { display: 'none' }
    }

    return {
      position: 'absolute',
      top: `${posicaoFixa.top}%`,
      left: `${posicaoFixa.left}%`,
      transform: 'translate(-50%, -100%)'
    }
  }

  onPostoClick (posto) {
    this.modalPostoAvancado.data = posto
    this.modalPostoAvancado.visible = true
  }

  closePostoAvancadoModal () {
    this.modalPostoAvancado.visible = false
    this.modalPostoAvancado.data = null
  }

  formatarNomePosto (posto) {
    // Use numero property that was extracted from name during loading
    const numeroPostoKey = posto.numero

    // Extract the description after "PA XX -"
    const nome = posto.name.replace(/^PA\s*\d{2}\s*-\s*/i, '')

    return `PA ${String(numeroPostoKey).padStart(2, '0')} - ${nome}`
  }

  isTopPin (posto) {
    const numeroPostoKey = posto.numero
    const posicaoFixa = this.posicoesFixas[numeroPostoKey]
    if (!posicaoFixa) return false
    return posicaoFixa.top < 25
  }

  onClienteChange (cliente) {
    this.$emit('cliente-change', cliente)
  }
}

export default MapViewerArea
</script>

<style lang="stylus" scoped>
@import '~quasar-variables'

.map-viewer-area
  width: 100%
  height: 100%
  overflow: auto

  .map-container
    position: relative
    width: 100%

    // SVG responsivo - preenche a largura e mantém aspect ratio
    .industrial-map-svg
      display: block
      width: 100%
      height: auto

    .map-loading-overlay
      position: absolute
      top: 0
      left: 0
      right: 0
      bottom: 0
      background: rgba(255, 255, 255, 0.9)
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      z-index: 2000
      border-radius: 4px

      .text-subtitle2
        color: $grey-7
        font-size: 14px

  // Wrapper do pin com tooltip
  .pin-wrapper
    position: absolute
    z-index: 100

    // Efeito de hover - zoom no pin
    &:hover
      z-index: 200

      .map-container-pin
        transform: scale(1.2)
        filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))

      .pin-tooltip
        opacity: 1
        visibility: visible
        transform: translateX(-50%) translateY(-10px)

  .map-container-pin
    cursor: pointer
    transition: all 0.3s ease
    transform-origin: center bottom

    // Escalar pins responsivamente
    svg
      width: 60px
      height: 70px

  // Tooltip
  .pin-tooltip
    position: absolute
    bottom: 100%
    left: 50%
    transform: translateX(-50%) translateY(0)
    background: white
    border-radius: 8px
    padding: 8px 12px
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15)
    opacity: 0
    visibility: hidden
    transition: all 0.3s ease
    pointer-events: none
    white-space: nowrap
    margin-bottom: 8px
    min-width: 150px

    // Seta do tooltip
    &::after
      content: ''
      position: absolute
      top: 100%
      left: 50%
      transform: translateX(-50%)
      border: 6px solid transparent
      border-top-color: white

    .tooltip-title
      font-size: 13px
      font-weight: 600
      color: #333
      margin-bottom: 4px

    .tooltip-status
      font-size: 12px
      font-weight: 600
      display: flex
      align-items: center
      gap: 4px

      &.critico
        color: #D41320

      &.atencao
        color: #EBB111

    // Variações de borda conforme criticidade
    &.tooltip-red
      border-left: 4px solid #D41320

    &.tooltip-yellow
      border-left: 4px solid #EBB111

    &.tooltip-green
      border-left: 4px solid #26A69A

    // Tooltip abaixo do pin para pins no topo do mapa
    &.tooltip-bottom
      bottom: auto
      top: 100%
      margin-bottom: 0
      margin-top: 8px

      &::after
        top: auto
        bottom: 100%
        border-top-color: transparent
        border-bottom-color: white

// Responsividade dos pins em telas menores
@media (max-width: 1400px)
  .map-viewer-area
    .map-container
      margin-top: 40px
    .map-container-pin
      svg
        width: 50px
        height: 58px

@media (max-width: 1000px)
  .map-viewer-area
    .map-container
      margin-top: 50px
    .map-container-pin
      svg
        width: 40px
        height: 47px
    .pin-tooltip
      min-width: 120px
      padding: 6px 10px
      .tooltip-title
        font-size: 11px
      .tooltip-status
        font-size: 10px

@media (max-width: 768px)
  .map-viewer-area
    .map-container
      margin-top: 60px
    .map-container-pin
      svg
        width: 32px
        height: 37px
    .pin-tooltip
      min-width: 100px
      padding: 4px 8px
      .tooltip-title
        font-size: 10px
      .tooltip-status
        font-size: 9px
</style>
